import { Coordinate } from '@mappedin/mappedin-js';
import type maplibregl from 'maplibre-gl';
import type { AgencyMode, TransitStop, VenueCoords } from '../types';
import { escapeHtml } from './escape-html';
import { hasMarkers, type MappedinMapView } from './mappedin-types';

const SOURCE_ID = 'transit-stops';
const HIDDEN_LAYER_ID = 'transit-stops-hidden';
const CLUSTER_RADIUS = 60;
const CLUSTER_MAX_ZOOM = 17;
const LABEL_ZOOM_THRESHOLD = 17;

const PILL_BG = 'rgba(12,15,24,0.90)';
const PILL_HIGHLIGHT_BG = 'rgba(12,15,24,1)';
const PILL_BORDER = '1.5px solid rgba(255,255,255,0.13)';
const PILL_SHADOW = '0 3px 12px rgba(0,0,0,0.45)';
const PILL_RADIUS = '22px';
const CIRCLE_SIZE = 30;
const HIGHLIGHT_FILTER = 'drop-shadow(0 0 10px rgba(73,144,243,0.8))';

const STOP_DATA_ATTR = 'data-stop-id';

type ClusterMode = 'bus' | 'rail' | 'bikeshare' | 'scootershare' | 'floating';

const CLUSTER_MODES: readonly ClusterMode[] = [
  'bus',
  'rail',
  'bikeshare',
  'scootershare',
  'floating',
];

const ICONS: Record<ClusterMode, string> = {
  bus: '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z"/></svg>',
  rail: '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2c-4 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h12v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-4-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-6H6V6h5v5zm2 0V6h5v5h-5zm3.5 6c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>',
  bikeshare:
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M15.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5zm5.8-10l2.4-2.4.8.8c1.3 1.3 3 2.1 5 2.1V9c-1.5 0-2.7-.6-3.6-1.5l-1.9-1.9c-.5-.4-1-.6-1.6-.6s-1.1.2-1.4.6L7.8 8.4c-.4.4-.6.9-.6 1.4 0 .6.2 1.1.6 1.4L11 14v5h2v-6.2l-2.2-2.3zM19 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5z"/></svg>',
  scootershare:
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M7.5 19.5c1.38 0 2.5-1.12 2.5-2.5s-1.12-2.5-2.5-2.5S5 15.62 5 17s1.12 2.5 2.5 2.5zm0-3.5c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm9 3.5c1.38 0 2.5-1.12 2.5-2.5s-1.12-2.5-2.5-2.5-2.5 1.12-2.5 2.5 1.12 2.5 2.5 2.5zm0-3.5c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM12 4l-3 7h5.5l-.9 3h2.1l.9-3H20l3-7H12zm5.3 5h-4.6l1.3-3h4.6l-1.3 3z"/></svg>',
  floating:
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>',
};

function getModeColor(mode: ClusterMode): string {
  switch (mode) {
    case 'bus':
      return '#2563eb';
    case 'bikeshare':
      return '#059669';
    case 'scootershare':
      return '#0d9488';
    case 'rail':
      return '#dc2626';
    case 'floating':
      return '#7c3aed';
    default:
      return '#6b7280';
  }
}

function normalizeMode(mode: AgencyMode): ClusterMode {
  switch (mode) {
    case 'rail':
    case 'subway':
    case 'train':
    case 'streetcar':
    case 'shuttle':
      return 'rail';
    case 'bus':
    case 'bikeshare':
    case 'scootershare':
    case 'floating':
      return mode;
    default:
      return 'bus';
  }
}

function modeCircleHtml(mode: ClusterMode): string {
  const color = getModeColor(mode);
  const icon = ICONS[mode];
  return `<div style="width:${CIRCLE_SIZE}px;height:${CIRCLE_SIZE}px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;flex-shrink:0;">${icon}</div>`;
}

const DIVIDER_HTML =
  '<div style="width:1px;height:18px;background:rgba(255,255,255,0.22);flex-shrink:0;"></div>';

function stopMarkerHtml(stop: TransitStop, showLabel: boolean): string {
  const mode = normalizeMode(stop.agencyMode);
  const label = showLabel
    ? `<div style="margin-left:6px;margin-right:4px;font-size:11px;font-weight:600;color:#fff;font-family:Figtree,sans-serif;white-space:nowrap;max-width:180px;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(stop.name)}</div>`
    : '';
  return `<div ${STOP_DATA_ATTR}="${escapeHtml(stop.id)}" style="display:inline-flex;align-items:center;padding:5px;cursor:pointer;background:${PILL_BG};border-radius:${PILL_RADIUS};border:${PILL_BORDER};box-shadow:${PILL_SHADOW};transition:opacity 0.15s, filter 0.15s;">${modeCircleHtml(mode)}${label}</div>`;
}

interface ClusterModeCounts {
  mode_bus: number;
  mode_rail: number;
  mode_bikeshare: number;
  mode_scootershare: number;
  mode_floating: number;
}

function readNumber(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}

function clusterMarkerHtml(modes: readonly ClusterMode[], pointCount: number): string {
  const parts = modes
    .map((mode, index) =>
      index === 0 ? modeCircleHtml(mode) : DIVIDER_HTML + modeCircleHtml(mode),
    )
    .join('');
  return `<div style="display:inline-flex;align-items:center;gap:5px;padding:5px 8px;cursor:pointer;background:${PILL_BG};border-radius:${PILL_RADIUS};border:${PILL_BORDER};box-shadow:${PILL_SHADOW};transition:opacity 0.15s;">${parts}<div style="margin-left:2px;padding:2px 8px;background:rgba(255,255,255,0.12);border-radius:12px;font-size:12px;font-weight:700;color:#fff;font-family:Figtree,sans-serif;">${pointCount}</div></div>`;
}

function venueMarkerHtml(name: string): string {
  return `<div style="padding:4px 12px;background:${PILL_BG};border-radius:8px;border:${PILL_BORDER};font-size:11px;font-family:Figtree,sans-serif;font-weight:600;color:#fff;white-space:nowrap;box-shadow:${PILL_SHADOW};">${escapeHtml(name)}</div>`;
}

function activeClusterModes(props: ClusterModeCounts): ClusterMode[] {
  const active = CLUSTER_MODES.filter((mode) => readNumber(props[`mode_${mode}` as const]) > 0);
  return active.length > 0 ? active : ['bus'];
}

function readClusterCounts(props: Record<string, unknown>): ClusterModeCounts {
  return {
    mode_bus: readNumber(props['mode_bus']),
    mode_rail: readNumber(props['mode_rail']),
    mode_bikeshare: readNumber(props['mode_bikeshare']),
    mode_scootershare: readNumber(props['mode_scootershare']),
    mode_floating: readNumber(props['mode_floating']),
  };
}

function coordinate(latitude: number, longitude: number): Coordinate {
  return new Coordinate({ latitude, longitude });
}

interface StopMarkerEntry {
  ref: unknown;
  showLabel: boolean;
}

interface ClusterMarkerEntry {
  ref: unknown;
}

interface StopFeatureCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: { type: 'Point'; coordinates: [number, number] };
    properties: {
      id: string;
      name: string;
      type: TransitStop['type'];
      walkMinutes: number;
      agencyName: string;
      agencyMode: AgencyMode;
      brandColor: string;
      normalizedMode: ClusterMode;
    };
  }>;
}

function toGeoJSON(stops: readonly TransitStop[]): StopFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: stops
      .filter((s) => s.type !== 'dockless')
      .map((stop) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [stop.longitude, stop.latitude] },
        properties: {
          id: stop.id,
          name: stop.name,
          type: stop.type,
          walkMinutes: stop.walkMinutes ?? -1,
          agencyName: stop.agencyName,
          agencyMode: stop.agencyMode,
          brandColor: stop.brandColor,
          normalizedMode: normalizeMode(stop.agencyMode),
        },
      })),
  };
}

type StopClickCallback = (stopId: string | null) => void;

/**
 * Manages Mappedin markers + MapLibre clustering for transit stops.
 *
 * Pattern (matches the original transit-app + web-applet):
 *   - The host MapLibre map (passed in as `mapLibreMap`) owns the outdoor
 *     basemap and the clustering source. We add a clustered GeoJSON
 *     source plus a hidden circle layer to force tile generation, then
 *     re-sync Mappedin markers on each `moveend` / source-data event.
 *   - Stops & clusters are rendered as Mappedin `Markers` on top of the
 *     `MapView` overlay so they project correctly with the 3D camera and
 *     stay aligned with indoor geometry.
 *   - Each marker ref is tagged via a WeakMap so `mapView.on('click')`
 *     can resolve back to its stop id.
 *   - When clustering can't be set up (e.g. mapLibreMap missing), all
 *     stops are dropped as direct Mappedin markers without clustering.
 *   - Highlight state drives inline styles via the `data-stop-id`
 *     attribute on each stop's HTML pill.
 */
export class TransitMarkerLayer {
  private mapView: MappedinMapView | null = null;
  private mapLibreMap: maplibregl.Map | null = null;
  private stopsById = new Map<string, TransitStop>();
  private stopMarkers = new Map<string, StopMarkerEntry>();
  private clusterMarkers = new Map<number, ClusterMarkerEntry>();
  private venueMarkerRef: unknown = null;
  private markerToStopId = new WeakMap<object, string>();

  private currentHighlight: string | null = null;
  private prevHighlightWrapper: HTMLElement | null = null;
  private prevHighlightZIndex = '';

  private mapMoveHandler: (() => void) | null = null;
  private mapDataHandler: ((event: maplibregl.MapSourceDataEvent) => void) | null = null;
  private mapViewClickHandler: ((event: unknown) => void) | null = null;
  private initialSyncTimer: number | null = null;

  private onStopClick: StopClickCallback | null = null;

  setOnStopClick(callback: StopClickCallback | null): void {
    this.onStopClick = callback;
  }

  attach(
    mapView: MappedinMapView,
    mapLibreMap: maplibregl.Map | null,
    stops: readonly TransitStop[],
    venueCoords: VenueCoords | null,
    venueName: string | null,
  ): void {
    this.detach();
    this.mapView = mapView;
    this.mapLibreMap = mapLibreMap;
    this.stopsById = new Map(stops.map((s) => [s.id, s]));

    if (venueCoords) {
      this.venueMarkerRef = mapView.Markers.add(
        coordinate(venueCoords.latitude, venueCoords.longitude),
        venueMarkerHtml(venueName ?? 'Venue'),
        { rank: 'always-visible', interactive: false },
      );
    }

    this.mapViewClickHandler = (event) => this.handleMapViewClick(event);
    mapView.on('click', this.mapViewClickHandler);

    const clusterableStops = stops.filter((s) => s.type !== 'dockless');
    if (clusterableStops.length === 0) return;

    if (!this.mapLibreMap) {
      this.placeStopsWithoutClustering(clusterableStops);
      return;
    }

    this.attachClusteringSource(this.mapLibreMap, clusterableStops);
  }

  detach(): void {
    const mapView = this.mapView;
    const mapLibreMap = this.mapLibreMap;

    if (this.initialSyncTimer != null) {
      window.clearTimeout(this.initialSyncTimer);
      this.initialSyncTimer = null;
    }

    if (mapView) {
      for (const entry of this.stopMarkers.values()) this.safeRemoveMarker(mapView, entry.ref);
      for (const entry of this.clusterMarkers.values()) this.safeRemoveMarker(mapView, entry.ref);
      if (this.venueMarkerRef) this.safeRemoveMarker(mapView, this.venueMarkerRef);
      if (this.mapViewClickHandler) {
        try {
          mapView.off('click', this.mapViewClickHandler);
        } catch {
          // mapView may already be torn down.
        }
      }
    }

    this.stopMarkers.clear();
    this.clusterMarkers.clear();
    this.venueMarkerRef = null;
    this.markerToStopId = new WeakMap();
    this.currentHighlight = null;
    this.prevHighlightWrapper = null;
    this.prevHighlightZIndex = '';
    this.stopsById = new Map();

    if (mapLibreMap) {
      if (this.mapMoveHandler) mapLibreMap.off('moveend', this.mapMoveHandler);
      if (this.mapDataHandler) mapLibreMap.off('sourcedata', this.mapDataHandler);
      try {
        if (mapLibreMap.getLayer(HIDDEN_LAYER_ID)) mapLibreMap.removeLayer(HIDDEN_LAYER_ID);
      } catch {
        // Layer may already be gone.
      }
      try {
        if (mapLibreMap.getSource(SOURCE_ID)) mapLibreMap.removeSource(SOURCE_ID);
      } catch {
        // Source may already be gone.
      }
    }

    this.mapMoveHandler = null;
    this.mapDataHandler = null;
    this.mapViewClickHandler = null;
    this.mapLibreMap = null;
    this.mapView = null;
  }

  highlight(stopId: string | null): void {
    this.currentHighlight = stopId;
    this.applyHighlight();
  }

  private placeStopsWithoutClustering(stops: readonly TransitStop[]): void {
    const mapView = this.mapView;
    if (!mapView) return;
    for (const stop of stops) {
      const ref = mapView.Markers.add(
        coordinate(stop.latitude, stop.longitude),
        stopMarkerHtml(stop, true),
        { rank: 'always-visible', interactive: true },
      );
      if (ref && typeof ref === 'object') this.markerToStopId.set(ref, stop.id);
      this.stopMarkers.set(stop.id, { ref, showLabel: true });
    }
  }

  private attachClusteringSource(
    mapLibreMap: maplibregl.Map,
    clusterableStops: readonly TransitStop[],
  ): void {
    mapLibreMap.addSource(SOURCE_ID, {
      type: 'geojson',
      data: toGeoJSON(clusterableStops),
      cluster: true,
      clusterRadius: CLUSTER_RADIUS,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      clusterProperties: {
        mode_bus: ['+', ['case', ['==', ['get', 'normalizedMode'], 'bus'], 1, 0]],
        mode_rail: ['+', ['case', ['==', ['get', 'normalizedMode'], 'rail'], 1, 0]],
        mode_bikeshare: ['+', ['case', ['==', ['get', 'normalizedMode'], 'bikeshare'], 1, 0]],
        mode_scootershare: ['+', ['case', ['==', ['get', 'normalizedMode'], 'scootershare'], 1, 0]],
        mode_floating: ['+', ['case', ['==', ['get', 'normalizedMode'], 'floating'], 1, 0]],
      },
    });

    mapLibreMap.addLayer({
      id: HIDDEN_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      paint: { 'circle-radius': 1, 'circle-opacity': 0 },
    });

    const sync = (): void => {
      this.syncMarkers();
    };
    this.mapMoveHandler = sync;
    this.mapDataHandler = (event) => {
      if (event.sourceId === SOURCE_ID && event.isSourceLoaded) sync();
    };
    mapLibreMap.on('moveend', this.mapMoveHandler);
    mapLibreMap.on('sourcedata', this.mapDataHandler);

    this.initialSyncTimer = window.setTimeout(sync, 300);
  }

  private handleMapViewClick(event: unknown): void {
    if (!this.onStopClick) return;
    if (hasMarkers(event)) {
      const markers = event.markers ?? [];
      for (const marker of markers) {
        if (marker && typeof marker === 'object') {
          const stopId = this.markerToStopId.get(marker);
          if (stopId) {
            this.onStopClick(stopId);
            return;
          }
        }
      }
    }
    this.onStopClick(null);
  }

  private syncMarkers(): void {
    const mapView = this.mapView;
    const mapLibreMap = this.mapLibreMap;
    if (!mapView || !mapLibreMap) return;
    if (!mapLibreMap.getSource(SOURCE_ID)) return;

    const showLabels = mapLibreMap.getZoom() > LABEL_ZOOM_THRESHOLD;
    const features = mapLibreMap.querySourceFeatures(SOURCE_ID);

    const seenClusters = new Set<number>();
    const seenStops = new Set<string>();

    for (const feature of features) {
      const props = feature.properties;
      if (!props || feature.geometry.type !== 'Point') continue;
      const [lng, lat] = feature.geometry.coordinates;
      if (typeof lng !== 'number' || typeof lat !== 'number') continue;

      if (props['cluster'] === true) {
        this.upsertClusterMarker(mapView, props, lng, lat, seenClusters);
      } else {
        this.upsertStopMarker(mapView, props, lng, lat, showLabels, seenStops);
      }
    }

    this.pruneStaleMarkers(mapView, seenClusters, seenStops);
    this.applyHighlight();
  }

  private upsertClusterMarker(
    mapView: MappedinMapView,
    props: Record<string, unknown>,
    lng: number,
    lat: number,
    seen: Set<number>,
  ): void {
    const clusterId = props['cluster_id'];
    if (typeof clusterId !== 'number' || seen.has(clusterId)) return;
    seen.add(clusterId);
    if (this.clusterMarkers.has(clusterId)) return;

    const modes = activeClusterModes(readClusterCounts(props));
    const pointCount = readNumber(props['point_count']);
    const ref = mapView.Markers.add(coordinate(lat, lng), clusterMarkerHtml(modes, pointCount), {
      rank: 'always-visible',
      interactive: true,
    });
    this.clusterMarkers.set(clusterId, { ref });
  }

  private upsertStopMarker(
    mapView: MappedinMapView,
    props: Record<string, unknown>,
    lng: number,
    lat: number,
    showLabels: boolean,
    seen: Set<string>,
  ): void {
    const stopId = props['id'];
    if (typeof stopId !== 'string' || seen.has(stopId)) return;
    seen.add(stopId);

    const existing = this.stopMarkers.get(stopId);
    if (existing && existing.showLabel === showLabels) return;
    if (existing) this.safeRemoveMarker(mapView, existing.ref);

    const stop = this.stopsById.get(stopId);
    if (!stop) return;

    const ref = mapView.Markers.add(coordinate(lat, lng), stopMarkerHtml(stop, showLabels), {
      rank: 'always-visible',
      interactive: true,
    });
    if (ref && typeof ref === 'object') this.markerToStopId.set(ref, stopId);
    this.stopMarkers.set(stopId, { ref, showLabel: showLabels });
  }

  private pruneStaleMarkers(
    mapView: MappedinMapView,
    seenClusters: Set<number>,
    seenStops: Set<string>,
  ): void {
    for (const [clusterId, entry] of this.clusterMarkers) {
      if (!seenClusters.has(clusterId)) {
        this.safeRemoveMarker(mapView, entry.ref);
        this.clusterMarkers.delete(clusterId);
      }
    }
    for (const [stopId, entry] of this.stopMarkers) {
      if (!seenStops.has(stopId)) {
        this.safeRemoveMarker(mapView, entry.ref);
        this.stopMarkers.delete(stopId);
      }
    }
  }

  private safeRemoveMarker(mapView: MappedinMapView, ref: unknown): void {
    try {
      mapView.Markers.remove(ref);
    } catch {
      // Marker may have already been torn down.
    }
  }

  private applyHighlight(): void {
    if (this.prevHighlightWrapper) {
      this.prevHighlightWrapper.style.zIndex = this.prevHighlightZIndex;
      this.prevHighlightWrapper = null;
      this.prevHighlightZIndex = '';
    }

    const highlight = this.currentHighlight;
    for (const stopId of this.stopMarkers.keys()) {
      const pill = document.querySelector<HTMLElement>(
        `[${STOP_DATA_ATTR}="${CSS.escape(stopId)}"]`,
      );
      if (!pill) continue;

      if (!highlight) {
        pill.style.opacity = '1';
        pill.style.filter = '';
        pill.style.background = PILL_BG;
        continue;
      }
      if (stopId === highlight) {
        pill.style.opacity = '1';
        pill.style.background = PILL_HIGHLIGHT_BG;
        pill.style.filter = HIGHLIGHT_FILTER;
        const wrapper = pill.closest<HTMLElement>('[style*="position"]') ?? pill.parentElement;
        if (wrapper) {
          this.prevHighlightZIndex = wrapper.style.zIndex;
          wrapper.style.zIndex = '9999';
          this.prevHighlightWrapper = wrapper;
        }
      } else {
        pill.style.opacity = '0.3';
        pill.style.filter = '';
        pill.style.background = PILL_BG;
      }
    }
  }
}
